import { selectIsInternalAdmin } from 'accessors'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import SmartDatepicker, {
  smartDateTypes
} from 'components/ReusableComponents/SmartDatepicker'
import Tab from 'components/ReusableComponents/Tabs'
import Toggle from 'components/ReusableComponents/Toggle'
import { parentChainNames } from 'main/hotelBrandsAndChains'
import moment from 'moment'
import { useEffect } from 'react'
import { useSelector } from 'react-redux'

import styles from './AnalyticTabs.module.scss'

const { thisWeek, last7days, last14days, last30days } = smartDateTypes

const AnalyticTabs = ({
  hasGroupHotelControls = true,
  showTabs = true,
  showWholeGroup,
  setShowWholeGroup,
  setShowAllHotels,
  showAllHotels,
  parentChain,
  setParentChain,
  setFromDate,
  setToDate,
  fromDate,
  toDate,
  isAllTime,
  setIsAllTime
}) => {
  const isInternalAdmin = useSelector(selectIsInternalAdmin)

  const isIndependentlyManaged = useSelector(s => s.hotelDetails.isIndependentlyManaged)

  const isGroupLevelUser = useSelector(s => s.login.isGroupLevelUser)

  const groupName = useSelector(s => s.hotelDetails.groupName)

  let hotelGroupOptions = {
    currentHotel: ' Current Hotel',
    wholeGroup: groupName || 'Whole Group'
  }

  if (isInternalAdmin && isIndependentlyManaged) {
    hotelGroupOptions = { currentHotel: ' Current Hotel', allHotels: 'All Hotels' }
  }

  if (isInternalAdmin && !isIndependentlyManaged) {
    hotelGroupOptions = { ...hotelGroupOptions, allHotels: 'All Hotels' }
  }

  function handleGroupOptionsChange(e) {
    const value = e.target.value

    if (value === 'wholeGroup') {
      setShowWholeGroup(true)
      setShowAllHotels(false)
    } else if (value === 'currentHotel') {
      setShowWholeGroup(false)
      setShowAllHotels(false)
    } else if (value === 'allHotels') {
      setShowAllHotels(true)
      setShowWholeGroup(false)
    }
  }

  let hotelGroupOptionsValue = 'currentHotel'

  if (showWholeGroup) {
    hotelGroupOptionsValue = 'wholeGroup'
  }

  if (showAllHotels) {
    hotelGroupOptionsValue = 'allHotels'
  }

  useEffect(() => {
    if (!showAllHotels && setIsAllTime) {
      setIsAllTime(false)
    }
  }, [showAllHotels, setIsAllTime])

  if (showTabs) {
    return (
      <Tab.Parent className={styles.tabs}>
        <Tab to="/analytics" exact style={{ marginRight: 2 }}>
          <svg
            width="22"
            height="22"
            viewBox="0 0 22 22"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9.16658 18.3333V12.8333H12.8333V18.3333H17.4166V11H20.1666L10.9999 2.75L1.83325 11H4.58325V18.3333H9.16658Z"
              fill="#A8ADB2"
            />
          </svg>
        </Tab>
        <Tab to="/analytics/subscriptions">Subscriptions</Tab>
        <Tab to="/analytics/outreach">Outreach</Tab>
        <Tab to="/analytics/OTA-outreach">OTA Outreach</Tab>
        <Tab to="/analytics/reviews">Reviews</Tab>
        {/* <Tab to="/analytics/engagement">Engagement</Tab> */}
        <Tab to="/analytics/conversions">Conversions</Tab>
        <div className={styles.datepickerHolder}>
          {isInternalAdmin && (showAllHotels || showWholeGroup) && (
            <Dropdown
              options={{
                all: ' Any brand',
                ...parentChainNames
              }}
              value={parentChain}
              onChange={e => setParentChain(e.target.value)}
            />
          )}
          {hasGroupHotelControls && (
            <>
              {(isInternalAdmin || isGroupLevelUser) && (
                <Dropdown
                  options={hotelGroupOptions}
                  onChange={handleGroupOptionsChange}
                  value={hotelGroupOptionsValue}
                  className={styles.hotelGroupOptionsDropdown}
                />
              )}
            </>
          )}
          <SmartDatepicker
            localstorageName="performance-reports"
            isRange={true}
            startDate={moment(fromDate)}
            endDate={moment(toDate)}
            minDate={isInternalAdmin ? null : moment('2021-07-13')}
            onChangeStartDate={date => setFromDate(date.format('YYYY-MM-DD'))}
            onChangeEndDate={date => setToDate(date.format('YYYY-MM-DD'))}
            smartDates={[thisWeek, last7days, last14days, last30days]}
            showMonths={isInternalAdmin ? true : false}
            showQuarters={isInternalAdmin ? true : false}
            className={styles.datepicker}
            textOverride={setIsAllTime && isAllTime ? 'All time' : null}
            extraSmartOptions={
              hotelGroupOptionsValue === 'allHotels' &&
              setIsAllTime && (
                <label className={styles.allTimeToggleHolder}>
                  <span className={styles.allTimeLabel}>All time</span>
                  <Toggle
                    checked={isAllTime}
                    handleChange={e => setIsAllTime(e.target.checked)}
                  />
                </label>
              )
            }
            dismissCurrentDay={true}
          />
        </div>
      </Tab.Parent>
    )
  }

  return (
    <div className={styles.datepickerHolder}>
      {isInternalAdmin && (showAllHotels || showWholeGroup) && (
        <Dropdown
          options={{
            all: ' Any brand',
            ...parentChainNames
          }}
          value={parentChain}
          onChange={e => setParentChain(e.target.value)}
        />
      )}
      {hasGroupHotelControls && (
        <>
          {(isInternalAdmin || isGroupLevelUser) && (
            <Dropdown
              options={hotelGroupOptions}
              onChange={handleGroupOptionsChange}
              value={hotelGroupOptionsValue}
              className={styles.hotelGroupOptionsDropdown}
            />
          )}
        </>
      )}
      <SmartDatepicker
        localstorageName="performance-reports"
        isRange={true}
        startDate={moment(fromDate)}
        endDate={moment(toDate)}
        minDate={isInternalAdmin ? null : moment('2021-07-13')}
        onChangeStartDate={date => setFromDate(date.format('YYYY-MM-DD'))}
        onChangeEndDate={date => setToDate(date.format('YYYY-MM-DD'))}
        smartDates={[thisWeek, last7days, last14days, last30days]}
        showMonths={isInternalAdmin ? true : false}
        showQuarters={isInternalAdmin ? true : false}
        className={styles.datepicker}
        textOverride={setIsAllTime && isAllTime ? 'All time' : null}
        extraSmartOptions={
          hotelGroupOptionsValue === 'allHotels' &&
          setIsAllTime && (
            <label className={styles.allTimeToggleHolder}>
              <span className={styles.allTimeLabel}>All time</span>
              <Toggle
                checked={isAllTime}
                handleChange={e => setIsAllTime(e.target.checked)}
              />
            </label>
          )
        }
      />
    </div>
  )
}

export default AnalyticTabs
